import { useEffect, useId, useMemo, useRef, useState } from "react";

import { axisMargin, formatTick, niceTicks } from "../lib/chart-scale.js";

/**
 * A time-series line chart to the routerreport dataviz spec: 2px lines, one
 * axis, hairline recessive gridlines, a crosshair that snaps to the nearest
 * sample, and a single tooltip listing every series at that x — never one
 * tooltip per line. Series identity always carries a visible text label
 * (never color alone): three of our validated categorical slots sit under
 * 3:1 contrast on the light surface, so the legend below is not optional
 * decoration, it is the accessibility channel.
 */

/**
 * Stroke patterns, applied by series position.
 *
 * Both period-correct -- an 80s pen plotter told series apart by pattern,
 * not hue -- and the secondary encoding the series palette's CVD margin
 * calls for, so the chart stays readable when hue alone does not carry.
 */
export const SERIES_DASH_PATTERNS = ["", "6 3", "2 3", "8 3 2 3", "4 2", "1 3"] as const;

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  /** Overrides the by-position dash pattern when a series needs a fixed one. */
  dash?: string;
  points: { x: number; y: number | null }[];
  /** Formats a value for the tooltip and y-axis, e.g. "12.3 Mbit/s". */
  format?: (value: number) => string;
}

export interface LineChartProps {
  series: ChartSeries[];
  height?: number;
  /** Fixed at 0 unless the data genuinely goes negative. */
  zeroBaseline?: boolean;
  yLabel?: string;
  /**
   * Axis tick formatter. Defaults to the first series' own formatter, so the
   * axis and the tooltip agree about units -- an axis reading "2,000,000"
   * beside a tooltip reading "2.00 Mbit/s" makes the reader do the conversion.
   * Passed explicitly where the tooltip's precision is too verbose for an axis.
   */
  yTickFormat?: (value: number) => string;
  emptyMessage?: string;
  /** Shown as a small corner note, e.g. "5m rollup" — transparency about
   * which resolution answered the query, since raw data ages out. */
  resolutionNote?: "raw" | "5m" | "1h";
}

const MARGIN = { top: 12, right: 16, bottom: 28 };
const TICK_FONT_SIZE = 11;

function formatTime(x: number): string {
  return new Date(x).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function LineChart({
  series,
  height = 260,
  zeroBaseline = true,
  yLabel,
  yTickFormat,
  emptyMessage = "NO DATA IN RANGE",
  resolutionNote,
}: LineChartProps) {
  const gradientId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  // Track container width so the SVG viewBox stays responsive without a
  // charting library's own resize machinery. useEffect, not useMemo: only
  // useEffect's return value is treated as a cleanup function by React, and
  // refs aren't reactive, so this has to run once after mount and read
  // containerRef.current then, not depend on it as if it were state.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.max(280, entry.contentRect.width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const allX = useMemo(
    () => [...new Set(series.flatMap((s) => s.points.map((p) => p.x)))].sort((a, b) => a - b),
    [series],
  );

  const hasData = allX.length > 0 && series.some((s) => s.points.some((p) => p.y !== null));

  const { yMin, yMax } = useMemo(() => {
    const values = series.flatMap((s) => s.points.map((p) => p.y).filter((y): y is number => y !== null));
    if (values.length === 0) return { yMin: 0, yMax: 1 };
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (zeroBaseline) min = Math.min(0, min);
    if (min === max) {
      min -= 1;
      max += 1;
    }
    const pad = (max - min) * 0.08;
    return { yMin: min - pad, yMax: max + pad };
  }, [series, zeroBaseline]);

  const ticks = useMemo(() => niceTicks(yMin, yMax), [yMin, yMax]);
  const tickMin = ticks[0]!;
  const tickMax = ticks[ticks.length - 1]!;

  // The axis speaks the same units as the tooltip unless told otherwise.
  const tickFormat = yTickFormat ?? series[0]?.format ?? formatTick;
  const tickLabels = useMemo(() => ticks.map(tickFormat), [ticks, tickFormat]);
  // Measured from the labels rather than fixed, so a megabit axis is not
  // clipped the way a fixed 56px margin clipped "2,000,000" to ",000,000".
  const marginLeft = useMemo(
    () => axisMargin(tickLabels, TICK_FONT_SIZE),
    [tickLabels],
  );

  const plotWidth = width - marginLeft - MARGIN.right;
  const plotHeight = height - MARGIN.top - MARGIN.bottom;

  const xMin = allX[0] ?? 0;
  const xMax = allX[allX.length - 1] ?? 1;
  const xScale = (x: number) =>
    marginLeft + (xMax === xMin ? plotWidth / 2 : ((x - xMin) / (xMax - xMin)) * plotWidth);
  const yScale = (y: number) =>
    MARGIN.top + plotHeight - ((y - tickMin) / (tickMax - tickMin)) * plotHeight;

  const pathFor = (points: ChartSeries["points"]) => {
    let d = "";
    let drawing = false;
    for (const point of points) {
      if (point.y === null) {
        drawing = false;
        continue;
      }
      const cmd = drawing ? "L" : "M";
      d += `${cmd}${xScale(point.x).toFixed(1)},${yScale(point.y).toFixed(1)} `;
      drawing = true;
    }
    return d.trim();
  };

  const handlePointerMove = (event: React.PointerEvent<SVGRectElement>) => {
    if (allX.length === 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const relX = event.clientX - rect.left;
    const targetX = xMin + (relX / plotWidth) * (xMax - xMin);
    // Nearest-sample snap so the reader aims at a time, not a 2px line.
    let closest = 0;
    let closestDist = Infinity;
    for (let i = 0; i < allX.length; i += 1) {
      const dist = Math.abs(allX[i]! - targetX);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    }
    setHoverIndex(closest);
  };

  const hoverX = hoverIndex !== null ? (allX[hoverIndex] ?? null) : null;
  const tooltipRows =
    hoverX !== null
      ? series
          .map((s) => {
            const point = s.points.find((p) => p.x === hoverX);
            return { series: s, value: point?.y ?? null };
          })
          .filter((row) => row.value !== null)
      : [];

  const showLegend = series.length >= 2;

  return (
    <div ref={containerRef} style={{ width: "100%", position: "relative" }}>
      {resolutionNote && resolutionNote !== "raw" && (
        <span
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            fontSize: 11,
            color: "var(--text-muted)",
            zIndex: 1,
          }}
        >
          [{resolutionNote} ROLLUP]
        </span>
      )}
      {!hasData ? (
        <div
          style={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-muted)",
            fontSize: 13,
            border: "1px solid var(--border)",
            background: "var(--surface-1)",
          }}
        >
          {`-- ${emptyMessage} --`}
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <svg
            width="100%"
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={yLabel ?? "Time series chart"}
          >
            {/* Recessive hairline gridlines, one step off the surface. */}
            {ticks.map((tick, index) => (
              <g key={tick}>
                <line
                  x1={marginLeft}
                  x2={width - MARGIN.right}
                  y1={yScale(tick)}
                  y2={yScale(tick)}
                  stroke="var(--gridline)"
                  strokeWidth={1}
                />
                <text
                  x={marginLeft - 8}
                  y={yScale(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={TICK_FONT_SIZE}
                  fill="var(--text-muted)"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {tickLabels[index]}
                </text>
              </g>
            ))}

            {/* Baseline / axis */}
            <line
              x1={marginLeft}
              x2={width - MARGIN.right}
              y1={MARGIN.top + plotHeight}
              y2={MARGIN.top + plotHeight}
              stroke="var(--baseline)"
              strokeWidth={1}
            />

            {series.map((s, index) => (
              <path
                key={s.key}
                d={pathFor(s.points)}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="butt"
                strokeDasharray={s.dash ?? SERIES_DASH_PATTERNS[index % SERIES_DASH_PATTERNS.length]}
              />
            ))}

            {/* Crosshair */}
            {hoverX !== null && (
              <line
                x1={xScale(hoverX)}
                x2={xScale(hoverX)}
                y1={MARGIN.top}
                y2={MARGIN.top + plotHeight}
                stroke="var(--text-muted)"
                strokeWidth={1}
              />
            )}
            {hoverX !== null &&
              tooltipRows.map((row) => (
                <circle
                  key={row.series.key}
                  cx={xScale(hoverX)}
                  cy={yScale(row.value!)}
                  r={4}
                  fill={row.series.color}
                  stroke="var(--surface-1)"
                  strokeWidth={2}
                />
              ))}

            {/* Transparent hit area, larger than the plotted marks. */}
            <rect
              x={marginLeft}
              y={MARGIN.top}
              width={plotWidth}
              height={plotHeight}
              fill="transparent"
              onPointerMove={handlePointerMove}
              onPointerLeave={() => setHoverIndex(null)}
            />
          </svg>

          {hoverX !== null && tooltipRows.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: MARGIN.top,
                left: Math.min(xScale(hoverX) + 12, width - 180),
                background: "var(--surface-raised)",
                border: "1px solid var(--border)",
                padding: "8px 10px",
                fontSize: 12,
                pointerEvents: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                minWidth: 140,
              }}
            >
              <div style={{ color: "var(--text-muted)", marginBottom: 4 }}>{formatTime(hoverX)}</div>
              {tooltipRows.map((row) => (
                <div
                  key={row.series.key}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}
                >
                  <svg width="12" height="4" aria-hidden focusable="false">
                    <line x1="0" y1="2" x2="12" y2="2" stroke={row.series.color} strokeWidth={2} />
                  </svg>
                  <span style={{ color: "var(--text-secondary)", flex: 1 }}>{row.series.label}</span>
                  <strong style={{ color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
                    {row.series.format ? row.series.format(row.value!) : formatTick(row.value!)}
                  </strong>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showLegend && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8, fontSize: 12 }}>
          {series.map((s, index) => (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <svg width="18" height="4" aria-hidden focusable="false">
                <line
                  x1="0"
                  y1="2"
                  x2="18"
                  y2="2"
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray={s.dash ?? SERIES_DASH_PATTERNS[index % SERIES_DASH_PATTERNS.length]}
                />
              </svg>
              <span style={{ color: "var(--text-secondary)" }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
